import type { SerializedNode, ConversionContext, RGBA, TRBLNumberCOORDS } from '../types';
import { getAttribute } from '../parser';
import {
  parseColor,
  parsePadding,
  parseBorderRadius,
  parseInteger,
  parseShadowAttributes,
  SHADOW_ATTRIBUTES,
} from '../utils';
import { FORM_DEFAULTS, getLeafFillSpace } from '../utils/defaults';
import { FORM_NODE_NAME } from '../../utils/constants';

/**
 * Parse breakpoint-prefixed attributes (tablet-*, mobile-*) from a form element
 *
 * @param element - The XML element
 * @returns Object with tablet and mobile override props
 */
const parseBreakpointAttributes = (
  element: Element,
): { tablet: Record<string, unknown>; mobile: Record<string, unknown> } => {
  const tablet: Record<string, unknown> = {};
  const mobile: Record<string, unknown> = {};

  // Convert NamedNodeMap to array for iteration
  const attrs = Array.from(element.attributes);

  for (const attr of attrs) {
    // Check for tablet- or mobile- prefix
    let prefix: 'tablet' | 'mobile' | null = null;
    let propName: string | null = null;

    if (attr.name.startsWith('tablet-')) {
      prefix = 'tablet';
      propName = attr.name.slice(7); // 'tablet-'.length = 7
    } else if (attr.name.startsWith('mobile-')) {
      prefix = 'mobile';
      propName = attr.name.slice(7); // 'mobile-'.length = 7
    }

    if (!prefix || !propName) continue;

    const target = prefix === 'tablet' ? tablet : mobile;

    switch (propName) {
      case 'padding':
        target.padding = parsePadding(attr.value, FORM_DEFAULTS.padding as TRBLNumberCOORDS);
        break;
      case 'margin':
        target.margin = parsePadding(attr.value, FORM_DEFAULTS.margin as TRBLNumberCOORDS);
        break;
      case 'borderRadius':
        target.borderRadius = parseBorderRadius(
          attr.value,
          FORM_DEFAULTS.borderRadius as TRBLNumberCOORDS,
        );
        break;
    }
  }

  return { tablet, mobile };
};

/**
 * Transform a <form> XML element into a CraftJS Form node
 *
 * @param element - The XML element
 * @param context - Conversion context
 * @param childIds - IDs of child nodes (form fields and submit button)
 * @returns SerializedNode for the Form
 */
export const transformForm = (
  element: Element,
  context: ConversionContext,
  childIds: string[],
): SerializedNode => {
  // Parse name
  const nameAttr = getAttribute(element, 'name');
  const name = nameAttr || FORM_DEFAULTS.name;

  // Parse colors
  const bgAttr = getAttribute(element, 'bg');
  const backgroundColor = bgAttr ? (parseColor(bgAttr) as RGBA) : FORM_DEFAULTS.backgroundColor;

  const borderColorAttr = getAttribute(element, 'borderColor');
  const borderColor = borderColorAttr
    ? (parseColor(borderColorAttr) as RGBA)
    : FORM_DEFAULTS.borderColor;

  const hoverBorderColorAttr = getAttribute(element, 'hoverBorderColor');
  const hoverBorderColor = hoverBorderColorAttr
    ? (parseColor(hoverBorderColorAttr) as RGBA)
    : undefined;

  // Parse spacing
  const paddingAttr = getAttribute(element, 'padding');
  const padding = parsePadding(paddingAttr, FORM_DEFAULTS.padding);

  const marginAttr = getAttribute(element, 'margin');
  const margin = parsePadding(marginAttr, FORM_DEFAULTS.margin);

  // Parse border
  const borderRadiusAttr = getAttribute(element, 'borderRadius');
  const borderRadius = parseBorderRadius(borderRadiusAttr, FORM_DEFAULTS.borderRadius);

  const borderWidthAttr = getAttribute(element, 'borderWidth');
  const borderWidth = parseInteger(borderWidthAttr, FORM_DEFAULTS.borderWidth);

  // Parse field-level styling from form attributes
  const fieldMarginAttr = getAttribute(element, 'fieldMargin');
  const fieldMargin = parsePadding(fieldMarginAttr, FORM_DEFAULTS.fieldMargin);

  const fieldBorderRadiusAttr = getAttribute(element, 'fieldBorderRadius');
  const fieldBorderRadius = parseBorderRadius(
    fieldBorderRadiusAttr,
    FORM_DEFAULTS.fieldBorderRadius,
  );

  const fieldInputPaddingAttr = getAttribute(element, 'fieldInputPadding');
  const fieldInputPadding = parsePadding(fieldInputPaddingAttr, FORM_DEFAULTS.fieldInputPadding);

  // Parse field label styling
  const fieldLabelFontFamilyAttr = getAttribute(element, 'fieldLabelFontFamily');
  const fieldLabelFontFamily = fieldLabelFontFamilyAttr || FORM_DEFAULTS.fieldLabelFontFamily;

  const fieldLabelFontSizeAttr = getAttribute(element, 'fieldLabelFontSize');
  const fieldLabelFontSize = fieldLabelFontSizeAttr || FORM_DEFAULTS.fieldLabelFontSize;

  const fieldLabelFontWeightAttr = getAttribute(element, 'fieldLabelFontWeight');
  const fieldLabelFontWeight = fieldLabelFontWeightAttr
    ? parseInt(fieldLabelFontWeightAttr, 10)
    : FORM_DEFAULTS.fieldLabelFontWeight;

  const fieldLabelColorAttr = getAttribute(element, 'fieldLabelColor');
  const fieldLabelColor = fieldLabelColorAttr
    ? (parseColor(fieldLabelColorAttr) as RGBA)
    : FORM_DEFAULTS.fieldLabelColor;

  // Parse field input styling
  const fieldInputFontFamilyAttr = getAttribute(element, 'fieldInputFontFamily');
  const fieldInputFontFamily = fieldInputFontFamilyAttr || FORM_DEFAULTS.fieldInputFontFamily;

  const fieldInputFontSizeAttr = getAttribute(element, 'fieldInputFontSize');
  const fieldInputFontSize = fieldInputFontSizeAttr || FORM_DEFAULTS.fieldInputFontSize;

  const fieldInputFontWeightAttr = getAttribute(element, 'fieldInputFontWeight');
  const fieldInputFontWeight = fieldInputFontWeightAttr
    ? parseInt(fieldInputFontWeightAttr, 10)
    : FORM_DEFAULTS.fieldInputFontWeight;

  const fieldInputTextColorAttr = getAttribute(element, 'fieldInputTextColor');
  const fieldInputTextColor = fieldInputTextColorAttr
    ? (parseColor(fieldInputTextColorAttr) as RGBA)
    : FORM_DEFAULTS.fieldInputTextColor;

  const fieldInputBackgroundColorAttr = getAttribute(element, 'fieldInputBackgroundColor');
  const fieldInputBackgroundColor = fieldInputBackgroundColorAttr
    ? (parseColor(fieldInputBackgroundColorAttr) as RGBA)
    : FORM_DEFAULTS.fieldInputBackgroundColor;

  const fieldInputBorderColorAttr = getAttribute(element, 'fieldInputBorderColor');
  const fieldInputBorderColor = fieldInputBorderColorAttr
    ? (parseColor(fieldInputBorderColorAttr) as RGBA)
    : FORM_DEFAULTS.fieldInputBorderColor;

  const fieldInputBorderWidthAttr = getAttribute(element, 'fieldInputBorderWidth');
  const fieldInputBorderWidth = parseInteger(
    fieldInputBorderWidthAttr,
    FORM_DEFAULTS.fieldInputBorderWidth,
  );

  const fieldInputHoverBackgroundColorAttr = getAttribute(
    element,
    'fieldInputHoverBackgroundColor',
  );
  const fieldInputHoverBackgroundColor = fieldInputHoverBackgroundColorAttr
    ? (parseColor(fieldInputHoverBackgroundColorAttr) as RGBA)
    : FORM_DEFAULTS.fieldInputHoverBackgroundColor;

  const fieldInputHoverBorderColorAttr = getAttribute(element, 'fieldInputHoverBorderColor');
  const fieldInputHoverBorderColor = fieldInputHoverBorderColorAttr
    ? (parseColor(fieldInputHoverBorderColorAttr) as RGBA)
    : FORM_DEFAULTS.fieldInputHoverBorderColor;

  // Parse helper text color
  const fieldHelperTextColorAttr = getAttribute(element, 'fieldHelperTextColor');
  const fieldHelperTextColor = fieldHelperTextColorAttr
    ? (parseColor(fieldHelperTextColorAttr) as RGBA)
    : FORM_DEFAULTS.fieldHelperTextColor;

  // Parse checkbox checked color
  const fieldCheckboxCheckedColorAttr = getAttribute(element, 'fieldCheckboxCheckedColor');
  const fieldCheckboxCheckedColor = fieldCheckboxCheckedColorAttr
    ? (parseColor(fieldCheckboxCheckedColorAttr) as RGBA)
    : FORM_DEFAULTS.fieldCheckboxCheckedColor;

  // Parse shadow attributes
  const shadow = parseShadowAttributes(element);

  // Parse breakpoint overrides
  const { tablet: tabletOverrides, mobile: mobileOverrides } = parseBreakpointAttributes(element);

  // Build props object
  const props: Record<string, unknown> = {
    id: FORM_DEFAULTS.id,
    name,
    followupAction: FORM_DEFAULTS.followupAction,
    fieldWidth: FORM_DEFAULTS.fieldWidth,
    fieldMaxWidth: FORM_DEFAULTS.fieldMaxWidth,
    fieldMinWidth: FORM_DEFAULTS.fieldMinWidth,
    fieldMargin,
    fieldBorderRadius,
    fieldInputPadding,
    fieldLabelFontFamily,
    fieldLabelFontSize,
    fieldLabelFontWeight,
    fieldLabelColor,
    fieldLabelInvalidColor: FORM_DEFAULTS.fieldLabelInvalidColor,
    fieldLabelSpacing: FORM_DEFAULTS.fieldLabelSpacing,
    fieldInputFontFamily,
    fieldInputFontSize,
    fieldInputFontWeight,
    fieldInputTextColor,
    fieldInputHoverTextColor: FORM_DEFAULTS.fieldInputHoverTextColor,
    fieldInputSpacing: FORM_DEFAULTS.fieldInputSpacing,
    fieldInputBackgroundColor,
    fieldInputBorderColor,
    fieldInputBorderWidth,
    fieldInputHoverBackgroundColor,
    fieldInputHoverBorderColor,
    fieldInputInvalidBackgroundColor: FORM_DEFAULTS.fieldInputInvalidBackgroundColor,
    fieldInputInvalidBorderColor: FORM_DEFAULTS.fieldInputInvalidBorderColor,
    fieldHelperTextColor,
    fieldHelperTextInvalidColor: FORM_DEFAULTS.fieldHelperTextInvalidColor,
    fieldCheckboxCheckedColor,
    fieldShadowType: FORM_DEFAULTS.fieldShadowType,
    fieldShadowSettings: FORM_DEFAULTS.fieldShadowSettings,
    shadowType: shadow?.shadowType ?? 'none',
    shadowSettings: shadow?.shadowSettings ?? { x: 0, y: 0, blur: 0, spread: 0, color: '#000000' },
    width: FORM_DEFAULTS.width,
    height: FORM_DEFAULTS.height,
    minWidth: FORM_DEFAULTS.minWidth,
    minHeight: FORM_DEFAULTS.minHeight,
    maxWidth: FORM_DEFAULTS.maxWidth,
    maxWidthUnit: FORM_DEFAULTS.maxWidthUnit,
    padding,
    margin,
    borderRadius,
    borderWidth,
    borderColor,
    backgroundColor,
    position: FORM_DEFAULTS.position,
    top: FORM_DEFAULTS.top,
    left: FORM_DEFAULTS.left,
    right: FORM_DEFAULTS.right,
    bottom: FORM_DEFAULTS.bottom,
    positionAnchorsState: FORM_DEFAULTS.positionAnchorsState,
    zIndex: FORM_DEFAULTS.zIndex,
    visibility: FORM_DEFAULTS.visibility,
    fillSpace: getLeafFillSpace(context.parentFlexDirection, 'no'),
    flexBasis: FORM_DEFAULTS.width,
    aspectRatioLock: FORM_DEFAULTS.aspectRatioLock,
    aspectRatio: FORM_DEFAULTS.aspectRatio,
    tablet: { ...tabletOverrides },
    mobile: { ...FORM_DEFAULTS.mobile, ...mobileOverrides },
  };

  // Only include optional props if they have values
  if (hoverBorderColor) {
    props.hoverBorderColor = hoverBorderColor;
  }

  return {
    type: { resolvedName: FORM_NODE_NAME },
    isCanvas: true,
    props,
    displayName: FORM_NODE_NAME,
    custom: {
      displayName: FORM_NODE_NAME,
      isLocked: false,
    },
    hidden: false,
    nodes: childIds,
    linkedNodes: {},
    parent: context.parentId,
  };
};

/**
 * Valid attributes for form elements
 */
export const FORM_ATTRIBUTES = [
  'name',
  'bg',
  'padding',
  'margin',
  'borderRadius',
  'borderWidth',
  'borderColor',
  'hoverBorderColor',
  'fieldMargin',
  'fieldBorderRadius',
  'fieldInputPadding',
  'fieldLabelFontFamily',
  'fieldLabelFontSize',
  'fieldLabelFontWeight',
  'fieldLabelColor',
  'fieldInputFontFamily',
  'fieldInputFontSize',
  'fieldInputFontWeight',
  'fieldInputTextColor',
  'fieldInputBackgroundColor',
  'fieldInputBorderColor',
  'fieldInputBorderWidth',
  'fieldInputHoverBackgroundColor',
  'fieldInputHoverBorderColor',
  'fieldHelperTextColor',
  'fieldCheckboxCheckedColor',
  // Shadow attributes
  ...SHADOW_ATTRIBUTES,
] as const;

/**
 * Attributes that can be overridden per breakpoint (tablet/mobile)
 * These map to CraftJS detachable properties for forms
 */
export const BREAKPOINT_FORM_ATTRIBUTES = ['padding', 'margin', 'borderRadius'] as const;

/**
 * Check if an attribute is valid for form elements
 * Handles both base attributes and breakpoint-prefixed attributes (tablet-*, mobile-*)
 */
export const isValidFormAttribute = (attr: string): boolean => {
  // Check base attributes
  if ((FORM_ATTRIBUTES as readonly string[]).includes(attr)) {
    return true;
  }

  // Check breakpoint-prefixed attributes (tablet-padding, mobile-margin, etc.)
  let propName: string | null = null;
  if (attr.startsWith('tablet-')) {
    propName = attr.slice(7);
  } else if (attr.startsWith('mobile-')) {
    propName = attr.slice(7);
  }

  if (propName) {
    return (BREAKPOINT_FORM_ATTRIBUTES as readonly string[]).includes(propName);
  }

  return false;
};
